import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  DataGrid,
  GridToolbar,
} from '@mui/x-data-grid';
import InfoIcon from '@mui/icons-material/Info';
import config from '../config';

function PublicProductLocations() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [columns, setColumns] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  const generateColumns = (sampleItem) => {
    if (!sampleItem?.data) return [];
    
    // Get all fields from the first item's data
    const fields = Object.keys(sampleItem.data);
    
    // Create column definitions for each field
    return fields.map(field => ({
      field,
      headerName: field,
      flex: field.toLowerCase().includes('description') ? 2 : 1,
    }));
  };

  useEffect(() => {
    fetchData();
  }, [paginationModel, filterModel]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        filters: JSON.stringify(filterModel),
      });

      const response = await fetch(`${config.apiUrl}/productlocations/public?${queryParams}`);
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await response.json();

      // Generate columns if we haven't yet
      if (result.items.length > 0 && columns.length === 0) {
        const newColumns = generateColumns(result.items[0]);
        setColumns(newColumns);
      }

      // Transform the data by flattening it
      const transformedData = result.items.map(item => ({
        _id: item._id,
        // Spread the data object to flatten the structure
        ...item.data
      }));

      setData(transformedData);
      setTotal(result.total);
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      backgroundColor: '#f5f7fa',
      minHeight: '100vh',
      py: { xs: 2, sm: 3 },  // Responsive padding
    }}>
    <Container maxWidth="xl" sx={{ mb: 4 }}>
      {/* Header Banner */}
      <Box sx={{ 
        backgroundColor: '#1976d2',
        color: 'white',
        py: { xs: 1.5, sm: 2 },  // Smaller padding on mobile
        px: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 1,
        boxShadow: 2,
        '& h1': {
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
        }
      }}>
        <Typography variant="h4" component="h1">
          Product Locations
        </Typography>
      </Box>
      
      {/* Info Box */}
      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 },  // Responsive padding
        mb: 3, 
        display: 'flex', 
        alignItems: 'flex-start',
        gap: { xs: 1, sm: 2 },  // Smaller gap on mobile
        backgroundColor: '#f8f9fa',
        flexDirection: { xs: 'column', sm: 'row' }  // Stack vertically on mobile
      }}>
        <InfoIcon color="primary" sx={{ 
          mt: { xs: 0, sm: 0.5 },
          alignSelf: { xs: 'center', sm: 'flex-start' }
        }} />
        <Box>
          <Typography variant="h6" gutterBottom>
            About This Tool
          </Typography>
          <Typography variant="body1" color="text.secondary">
            This tool provides a searchable database of product locations. Use the search bar to find 
            specific items by part number, location, or description. The table supports filtering, 
            sorting, and column customization to help you quickly find what you need.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ 
        width: '100%', 
        overflow: 'hidden',
        '& .MuiDataGrid-root': {
          overflowX: 'auto',
          '@media (max-width: 600px)': {
            '& .MuiDataGrid-main': {
              width: 'fit-content',
              minWidth: '100%'
            }
          }
        }
      }}>
        {loading && <LinearProgress />}
        <DataGrid
          rows={data}
          getRowId={(row) => row._id}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          pageSizeOptions={[10, 25, 50, 100]}  // Add smaller page size for mobile
          rowCount={total}
          paginationMode="server"
          filterMode="server"
          density="compact"
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
              sx: {
                flexWrap: 'wrap',
                gap: 1,
                '@media (max-width: 600px)': {
                  justifyContent: 'center'
                }
              }
            },
          }}
          sx={{
            minHeight: 500,
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              padding: 1,
              fontSize: { xs: '0.875rem', sm: 'inherit' }
            },
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: '#f5f5f5',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#e3f2fd',
            },
            borderRadius: 1,
            boxShadow: 1
          }}
        />
      </Paper>
    </Container>
    </Box>
  );
}

export default PublicProductLocations; 